/**
 * capiPixelCanon.ts — single source of truth for Meta/TikTok CAPI row/form/
 * drawer text and option lists, mirrored 1:1 from the ratified canon mockup
 * (WEB-763, ../web2app-spec/design/project-settings-mockup/psettings-app.jsx,
 * commit 9798e9a "PixelAdvancedDrawer PII (WEB-743)"):
 *   - PIXEL_PROVIDERS (meta/tiktok entries, ~lines 661-668) — row label,
 *     letter, color, field labels/placeholders.
 *   - OUR_EVENTS / NET_EVENTS (~lines 695-708) — event-mapping section of
 *     PixelAdvancedDrawer.
 *
 * Kept in one file so CapiProviderRow / CapiConnectForm / PixelAdvancedDrawer
 * never drift from each other on network label/color/letter.
 */
import type { CapiNetwork } from '@/types/capi';

/**
 * Имена сетей УКОРОЧЕНЫ по макету 17.09 («Meta Pixel & Conversion API» →
 * «Meta», `psettings-app.jsx:659,663`). Причина владельца: длинный хвост
 * занимал половину строки и переносил живой статус на второй ряд, а человеку
 * важна сеть, а не название её продукта. Правка видна ВЕЗДЕ, где выводится
 * имя, — это и есть смысл канона: одна правда на строку, форму, шторку и
 * тексты подтверждений.
 */
export const CAPI_NETWORK_LABEL: Record<CapiNetwork, string> = {
  meta: 'Meta',
  tiktok: 'TikTok',
};

export const CAPI_NETWORK_LETTER: Record<CapiNetwork, string> = {
  meta: 'M',
  tiktok: 'T',
};

export const CAPI_NETWORK_COLOR: Record<CapiNetwork, string> = {
  meta: '#0866FF',
  tiktok: '#161616',
};

// WEB-3756 сверка пикселей 1, М8: CAPI_PIXEL_ID_PLACEHOLDER снят — у поля
// Pixel ID плейсхолдера нет (эталон settings-k.jsx Net).

/** WEB-1874: подпись поля токена — ключ словаря кабинета, не литерал. */
export const CAPI_TOKEN_LABEL_KEY: Record<
  CapiNetwork,
  'capiTokenLabelMeta' | 'capiTokenLabelTiktok'
> = {
  meta: 'capiTokenLabelMeta',
  tiktok: 'capiTokenLabelTiktok',
};

export const CAPI_TOKEN_PLACEHOLDER: Record<CapiNetwork, string> = {
  meta: 'EAAB…',
  tiktok: 'act.…',
};

export const CAPI_TEST_EVENT_PLACEHOLDER: Record<CapiNetwork, string> = {
  meta: 'TEST12345',
  tiktok: 'TEST123',
};

/** Row header hint shown while the row is toggled open but not yet connected. */
export function capiOpenHint(s: { capiOpenHint: string }): string {
  return s.capiOpenHint;
}

/*
 * WEB-2491 кабинетная часть, заход К6в (шов Ш-3): список строк «Имена
 * событий» (`CAPI_OUR_EVENTS`, `CapiOurEventKey`), имена сетей и умолчания
 * (`CAPI_NET_EVENTS`, `defaultEventMapping`) и перекладка в ключи канона
 * (`eventMappingFromBackend`, `eventMappingToBackend`) СНЯТЫ: форма берёт их
 * из справочника сервера `GET /capi/event-catalog` (`capiEventPairs.ts`).
 * Вернуть копию не даёт сторож `__tests__/capiNoCanonCopy.web2491.test.ts`.
 */

/**
 * РЕВИЗИЯ 46 (Н1) — УМОЛЧАНИЕ СТРОКИ «ПРОШЁЛ 90 %», ДЕЙСТВОВАВШЕЕ 18–21.09.2026.
 *
 * Реестр, а не число в тексте миграции: по этим значениям миграция данных
 * 1900000000292 узнаёт подключения, которым имя события досталось умолчанием
 * (а не выбором человека), и возвращает им то имя, что реально уходило в
 * сеть. Второй конец — сторож
 * `backend/src/shared/database/progress90-legacy-default.revision46.spec.ts`:
 * он требует, чтобы эти имена стояли в SQL миграции И чтобы они НЕ совпадали
 * с сегодняшним умолчанием (иначе миграция начала бы переписывать осознанный
 * выбор).
 *
 * Трогать этот реестр после выката миграции незачем: он описывает ПРОШЛОЕ.
 */
export const CAPI_PROGRESS90_LEGACY_DEFAULTS: Record<CapiNetwork, string> = {
  meta: 'Lead',
  tiktok: 'SubmitForm',
};

/**
 * WEB-1364 (половина «чтение») — ОДНА правда чтения на всех читателей
 * настройки пикселя: и на форму подключения, и на карточку подключения.
 *
 * Сервер хранит event-конфиг в двух формах (backend/src/attribution/utils/
 * capi-event-config.ts): плоский словарь имён и расширенная v1
 * `{version:1, events:{…}, valueMultiplier, firstPurchaseOnly}`. Кабинет читал
 * только верхний уровень: v1 он не понимал и подставлял умолчания, а карточка
 * выводила служебные ключи как «события». Здесь обе формы сводятся к одному
 * плоскому словарю имён; всё, что не строка, отбрасывается — сломанная
 * настройка не имеет права уронить экран.
 */
export function capiEventNamesFromBackend(
  backendMapping: Record<string, unknown> | null | undefined,
): Record<string, string> {
  if (!backendMapping || typeof backendMapping !== 'object') return {};
  const source =
    'events' in backendMapping ? backendMapping.events : backendMapping;
  if (!source || typeof source !== 'object' || Array.isArray(source)) return {};
  const result: Record<string, string> = {};
  for (const [key, value] of Object.entries(source as Record<string, unknown>)) {
    if (typeof value === 'string') result[key] = value;
  }
  return result;
}

/**
 * WEB-2224 п.1 + WEB-2218 — НАСТРОЙКИ ПОДКЛЮЧЕНИЯ, КОТОРЫЕ НЕ ЯВЛЯЮТСЯ
 * ИМЕНАМИ СОБЫТИЙ: блоки «Что отправляем» и «Деньги» формы подключения.
 *
 * Все четыре живут в той же jsonb-колонке подключения, что и имена событий
 * (`capi_connection.event_mapping`, расширенная v1-форма) — второй колонки
 * сервер не заводил, см. `backend/src/attribution/utils/capi-event-config.ts`.
 */
export interface CapiPixelSettings {
  /** Множитель ценности покупки. 1 = сумма уходит как есть. */
  valueMultiplier: number;
  /** true = «Только первую оплату человека»; false = «Все оплаты, включая продления». */
  firstPurchaseOnly: boolean;
  /** Отправлять возвраты и проигранные споры как отмену конверсии. */
  sendRefunds: boolean;
  /** «Слать события в обход блокировщиков» — дубль событий воронки с сервера. */
  serverFunnelEvents: boolean;
}

/**
 * Умолчания названы ОДИН раз и совпадают с серверными
 * (`EMPTY_CAPI_EVENT_CONFIG` + правило «возвраты включены, пока не сказано
 * иное»). Второй копии чисел на фронте быть не должно.
 */
export const CAPI_PIXEL_SETTINGS_DEFAULTS: CapiPixelSettings = {
  valueMultiplier: 1,
  firstPurchaseOnly: false,
  sendRefunds: true,
  serverFunnelEvents: false,
};

/**
 * Границы множителя. Верхняя — та же, что проверяет сервер
 * (`isValidValueMultiplier`: `> 0 && <= 1000`). Нижняя — ТОЛЬКО шаг ввода
 * руками в форме: сервер принимает любое положительное, и сохранённое
 * значение ниже 0.1 кабинет обязан пронести как есть (свежий взгляд 44:
 * форма молча возвращала 0.05 к единице — ценность конверсии ×20).
 */
export const CAPI_VALUE_MULTIPLIER_MIN = 0.1;
export const CAPI_VALUE_MULTIPLIER_MAX = 1000;

/**
 * Множитель на отправку: валидное по правилу СЕРВЕРА значение уходит как
 * есть; невалидное прижимается к ближней границе, а не подменяется единицей
 * (молчаливая подмена именно на 1 — то, от чего докблок формы обещал беречь).
 * Не число — умолчание.
 */
export function capiValueMultiplierToBackend(value: number): number {
  if (!Number.isFinite(value)) return CAPI_PIXEL_SETTINGS_DEFAULTS.valueMultiplier;
  if (value > 0 && value <= CAPI_VALUE_MULTIPLIER_MAX) return value;
  if (value <= 0) return CAPI_VALUE_MULTIPLIER_MIN;
  return CAPI_VALUE_MULTIPLIER_MAX;
}

/**
 * Чтение расширенных полей. Как и у чтения имён: НИЧЕГО не бросает, мусор
 * отбрасывается поштучно и заменяется умолчанием — сломанная настройка не
 * имеет права уронить экран настроек.
 */
export function capiSettingsFromBackend(
  backendMapping: Record<string, unknown> | null | undefined,
): CapiPixelSettings {
  if (!backendMapping || typeof backendMapping !== 'object') {
    return { ...CAPI_PIXEL_SETTINGS_DEFAULTS };
  }
  const raw = backendMapping as Record<string, unknown>;
  const multiplier = raw.valueMultiplier;
  return {
    valueMultiplier:
      typeof multiplier === 'number' &&
      Number.isFinite(multiplier) &&
      multiplier > 0 &&
      multiplier <= CAPI_VALUE_MULTIPLIER_MAX
        ? multiplier
        : CAPI_PIXEL_SETTINGS_DEFAULTS.valueMultiplier,
    // Включает РОВНО true, как на сервере: «правдоподобный» мусор ('да', 1)
    // не включает — цена ошибки здесь потерянные конверсии у клиента.
    firstPurchaseOnly: raw.firstPurchaseOnly === true,
    // Возвраты — единственное поле с умолчанием ВКЛЮЧЕНО: отсутствие ключа
    // значит «включено», выключение хранится явным false.
    sendRefunds: raw.sendRefunds !== false,
    serverFunnelEvents: raw.serverFunnelEvents === true,
  };
}

/**
 * Запись ВСЕЙ настройки подключения одним телом v1-формы.
 *
 * ⚠ Почему не плоский словарь, как раньше. Сервер сливает входящее с
 * сохранённым и наследует то, чего во входящем НЕТ (`mergeCapiEventConfigRaw`,
 * WEB-1364 п.1б). Плоский словарь имён не несёт служебных полей вовсе —
 * значит после пачки 43.1 форма их не стирает, но и задать не может: ровно
 * дефект WEB-2224 п.1. Поэтому все четыре поля уезжают ЯВНО, в том числе
 * выключенные: пропущенное `false` сервер прочитает как «не сказано» и
 * оставит прежнее true — снять галочку стало бы невозможно.
 */
export function capiConfigToBackend(
  settings: CapiPixelSettings,
): Record<string, unknown> {
  const multiplier = capiValueMultiplierToBackend(settings.valueMultiplier);
  // WEB-2491 К6в: имён событий здесь нет — форма кладёт в `events` заплатку
  // «только тронутое» (`capiEventPairsPatch`), сервер сливает по ключам.
  return {
    version: 1,
    valueMultiplier: multiplier,
    firstPurchaseOnly: settings.firstPurchaseOnly,
    sendRefunds: settings.sendRefunds,
    serverFunnelEvents: settings.serverFunnelEvents,
  };
}

/* ────────────────────────────────────────────────────────────────────────────
 * WEB-2121 — подвкладка «Аналитика» и витрина «Готовим».
 *
 * Записи лежат здесь, а не в отдельном файле, по той же причине, по которой
 * тут живут Meta и TikTok: цвет, буква и имя строки обязаны быть ОДНОЙ правдой
 * для строки, формы и текстов подтверждений. Источник — тот же макет
 * (`psettings-app.jsx`, ADS_SOON `:668-676`, ANALYTICS_SOON `:677-682`,
 * AmplitudeRow `:1049-1173`).
 * ──────────────────────────────────────────────────────────────────────────── */

/** Цвет значка Amplitude из макета (`:1085`). */
export const AMPLITUDE_COLOR = '#1E61F0';
/** Буква значка. */
export const AMPLITUDE_LETTER = 'A';
/** Имя строки. Торговая марка — не переводится и в словаре не живёт. */
export const AMPLITUDE_LABEL = 'Amplitude';

/** Ключ галочки в форме (кабинетный), `code` — имя события на сервере. */
export type AmplitudeEventKey =
  | 'open'
  | 'start'
  | 'step'
  | 'answer'
  | 'email'
  | 'finish'
  | 'paywall'
  | 'pay';

export type AmplitudeEventLabelKey =
  | 'amplitudeEventOpen'
  | 'amplitudeEventStart'
  | 'amplitudeEventStep'
  | 'amplitudeEventAnswer'
  | 'amplitudeEventEmail'
  | 'amplitudeEventFinish'
  | 'amplitudeEventPaywall'
  | 'amplitudeEventPay';

/**
 * Восемь событий, которыми клиент управляет галочками (макет `:1031-1040`).
 * Коды — наши серверные имена событий из карты WEB-2120 (§4, таблица из 12
 * событий). Остальные коды этой карты галочкой НЕ управляются — в макете
 * их нет; они перечислены реестром `AMPLITUDE_ALWAYS_EVENTS` ниже (WEB-2413
 * Д1), число здесь не повторяется.
 */
export const AMPLITUDE_EVENTS: ReadonlyArray<{
  key: AmplitudeEventKey;
  labelKey: AmplitudeEventLabelKey;
  code: string;
}> = [
  { key: 'open', labelKey: 'amplitudeEventOpen', code: 'QUIZ_VIEW' },
  { key: 'start', labelKey: 'amplitudeEventStart', code: 'QUIZ_START' },
  { key: 'step', labelKey: 'amplitudeEventStep', code: 'SCREEN_VIEW' },
  { key: 'answer', labelKey: 'amplitudeEventAnswer', code: 'SCREEN_ANSWER' },
  { key: 'email', labelKey: 'amplitudeEventEmail', code: 'EMAIL_SUBMIT' },
  { key: 'finish', labelKey: 'amplitudeEventFinish', code: 'QUIZ_COMPLETE' },
  { key: 'paywall', labelKey: 'amplitudeEventPaywall', code: 'PAYWALL_VIEW' },
  { key: 'pay', labelKey: 'amplitudeEventPay', code: 'CHECKOUT_SUCCESS' },
];

/**
 * WEB-2413 Д1 (решение `2387-Р14` «честная подпись») — события карты воронки
 * сервера, у которых в форме НЕТ галочки: они уходят всегда, «Снять все» их
 * не останавливает. Подпись под галочками (`amplitudeEventsAlways`)
 * собирается из подписей этого реестра, а не набирается числом руками: так
 * она уже разъехалась — называла четыре события, когда сервер слал пять
 * (уход на свою оплату, WEB-2164, добавили без подписи).
 *
 * Множество сверяет с сервером сторож двух концов
 * `__tests__/amplitude-events-always.twoEnds.web2413.guard.test.ts`: карта
 * `FUNNEL_EVENT_NAMES` минус служебные и запрещённые минус коды галочек
 * `AMPLITUDE_EVENTS`, условия — по `isEventEnabled`. Порядок — шаги оплаты.
 */
export type AmplitudeAlwaysLabelKey =
  | 'amplitudeAlwaysPriceClick'
  | 'amplitudeAlwaysCheckoutStart'
  | 'amplitudeAlwaysCheckoutFailed'
  | 'amplitudeAlwaysPaymentUnavailable'
  | 'amplitudeAlwaysPaywallHandoff';

export const AMPLITUDE_ALWAYS_EVENTS: ReadonlyArray<{
  code: string;
  labelKey: AmplitudeAlwaysLabelKey;
}> = [
  { code: 'PRICE_CLICK', labelKey: 'amplitudeAlwaysPriceClick' },
  { code: 'CHECKOUT_START', labelKey: 'amplitudeAlwaysCheckoutStart' },
  { code: 'CHECKOUT_FAILED', labelKey: 'amplitudeAlwaysCheckoutFailed' },
  { code: 'PAYMENT_UNAVAILABLE', labelKey: 'amplitudeAlwaysPaymentUnavailable' },
  { code: 'PAYWALL_HANDOFF', labelKey: 'amplitudeAlwaysPaywallHandoff' },
];

/** Витринная строка: сеть видно, но нажать нечего (урок L52). */
export interface ShowcaseNetwork {
  key: string;
  /** Торговая марка — не переводится. */
  label: string;
  /**
   * Ключ словаря, если в имени есть непереводимый только наполовину союз.
   * Пока такой ровно один — «Google Analytics и GTM»; держать его литералом в
   * коде нельзя (правило двух языков).
   */
  labelKey?: 'analyticsSoonGaLabel';
  color: string;
  letter: string;
  /** Светлый значок требует тёмной буквы, иначе её не видно. */
  darkLetter?: boolean;
}

/** «Готовим» на подвкладке «Рекламные источники» (макет `:668-676`). */
export const ADS_SOON_NETWORKS: ReadonlyArray<ShowcaseNetwork> = [
  { key: 'snapchat', label: 'Snapchat', color: '#FFFC00', letter: 'S', darkLetter: true },
  { key: 'pinterest', label: 'Pinterest', color: '#E60023', letter: 'P' },
  { key: 'axon', label: 'AppLovin (Axon)', color: '#0A6CFF', letter: 'A' },
  { key: 'openai', label: 'OpenAI', color: '#0FA37F', letter: 'O' },
  { key: 'taboola', label: 'Taboola', color: '#004B7A', letter: 'T' },
  { key: 'outbrain', label: 'Outbrain', color: '#F18421', letter: 'O' },
  { key: 'newsbreak', label: 'NewsBreak', color: '#D6091A', letter: 'N' },
];

/** «Готовим» на подвкладке «Аналитика» (макет `:677-682`). */
export const ANALYTICS_SOON_NETWORKS: ReadonlyArray<ShowcaseNetwork> = [
  { key: 'posthog', label: 'PostHog', color: '#1D4AFF', letter: 'P' },
  { key: 'mixpanel', label: 'Mixpanel', color: '#7856FF', letter: 'M' },
  {
    key: 'ga',
    label: 'Google Analytics',
    labelKey: 'analyticsSoonGaLabel',
    color: '#F9AB00',
    letter: 'G',
  },
  { key: 'clarity', label: 'Clarity', color: '#1E8EFA', letter: 'C' },
];

/**
 * WEB-2241: движок отправки возвратов в рекламные сети на сервере ЕСТЬ —
 * тумблер «Отправлять возвраты» в форме снова виден и управляет поведением.
 *
 * Что стоит за флагом (чтобы его не переключили обратно вслепую): при
 * ПОЛНОМ возврате и проигранном споре сервер ставит в очередь отмену
 * конверсии (Meta — событие `Refund`, TikTok — `CancelOrder`), уважает этот
 * тумблер и оставляет в журнале отправок строку-решение, когда не шлёт.
 * Частичный возврат в сеть не уходит вовсе (критерий владельца 18.09.2026).
 * Сервер: `backend/src/attribution/utils/refund-conversion.ts` +
 * `attribution/processors/capi-dispatch.processor.ts`.
 */
export const CAPI_REFUNDS_ENGINE_READY = true;
